/**
 * LR Direct Block 6B — Shipper Credit App Review Panel
 *
 * Read-only surface for the shipper company admin on an awarded LR Direct
 * load. Lists generated broker credit application PDFs with metadata and a
 * download button per fill.
 *
 * NO approve, NO sign, NO email, NO realtime, NO AI. Review only.
 */

import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import {
  ClipboardCheck, Download, FileText, AlertTriangle,
  Loader2, History, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface FillRow {
  id: string;
  load_id: string;
  broker_company_id: string;
  shipper_company_id: string;
  source_template_id: string;
  storage_path: string;
  filled_field_count: number;
  skipped_field_count: number;
  unsupported_types: string[];
  status: string;
  error_message: string | null;
  generation_source: string;
  generated_by: string | null;
  generated_at: string;
}

interface Props {
  loadId: string;
  className?: string;
}

export function ShipperCreditAppReviewPanel({ loadId, className }: Props) {
  const [fills, setFills] = useState<FillRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);

  const loadFills = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("broker_credit_app_fills")
      .select("id, load_id, broker_company_id, shipper_company_id, source_template_id, storage_path, filled_field_count, skipped_field_count, unsupported_types, status, error_message, generation_source, generated_by, generated_at")
      .eq("load_id", loadId)
      .order("generated_at", { ascending: false });
    if (!error && data) setFills(data as FillRow[]);
    setLoading(false);
  }, [loadId]);

  useEffect(() => { loadFills(); }, [loadFills]);

  const handleOpen = async (fill: FillRow) => {
    if (!fill.storage_path) return;
    setOpening(fill.id);
    try {
      const { data, error } = await supabase.storage
        .from("credit-app-fills")
        .createSignedUrl(fill.storage_path, 60);
      if (error || !data?.signedUrl) {
        toast.error("Could not open PDF — try again or contact support");
        return;
      }
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } finally {
      setOpening(null);
    }
  };

  const latest = fills[0];
  const history = fills.slice(1);

  return (
    <div className={cn("rounded-lg border border-border bg-card p-4", className)}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-bold uppercase tracking-wide text-foreground">
            Broker Credit Application — Review
          </h4>
        </div>
        <Badge variant="secondary" className="text-[10px]">Read only</Badge>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      ) : !latest ? (
        <div className="rounded-md border border-dashed border-border bg-background px-3 py-4 text-center">
          <FileText className="h-4 w-4 mx-auto text-muted-foreground mb-1.5" />
          <p className="text-xs text-muted-foreground">
            The broker has not generated a credit application yet.
          </p>
        </div>
      ) : (
        <>
          {/* Latest fill */}
          {latest.status === "generated" ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 mb-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="text-[10px] gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Latest
                    </Badge>
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {format(new Date(latest.generated_at), "MMM d, yyyy h:mm a")}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground space-y-0.5">
                    <div>
                      Source: <span className="text-foreground">{latest.generation_source}</span>
                      {" · "}
                      Mapped: <span className="text-foreground">{latest.filled_field_count}</span>
                      {" · "}
                      Skipped: <span className="text-foreground">{latest.skipped_field_count}</span>
                    </div>
                    {latest.unsupported_types?.length > 0 && (
                      <div>Unsupported: {latest.unsupported_types.join(", ")}</div>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={() => handleOpen(latest)}
                  disabled={opening === latest.id}
                >
                  {opening === latest.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Open PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                <Badge variant="destructive" className="text-[10px]">Generation failed</Badge>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {format(new Date(latest.generated_at), "MMM d, yyyy h:mm a")}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {latest.error_message || "Unknown error"}
              </p>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <History className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  History ({history.length})
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {history.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-2.5 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {f.status === "generated" ? (
                          <Badge variant="secondary" className="text-[10px]">Generated</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">Failed</Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          {format(new Date(f.generated_at), "MMM d, h:mm a")}
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {f.status === "generated"
                          ? `Mapped ${f.filled_field_count} · Skipped ${f.skipped_field_count}`
                          : (f.error_message || "Unknown error")}
                      </div>
                    </div>
                    {f.status === "generated" && f.storage_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 shrink-0 h-7 text-[11px]"
                        onClick={() => handleOpen(f)}
                        disabled={opening === f.id}
                      >
                        {opening === f.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        Open
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
